import type { FC } from "react"
import { memo, useMemo, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import Fuse from "fuse.js"
import {
	IconCheckOutline18 as Check,
	IconPlusOutline18 as Plus,
} from "nucleo-ui-outline-18"
import { FixedSizeList } from "react-window"

import { cn } from "@productlane/lib/src/cn"
import { Button, IconMap, Popover } from "@productlane/ui"
import {
	Command,
	CommandInput,
	CommandItem,
	CommandList,
} from "@productlane/ui/src/components/internal/command"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import { api } from "@/lib/api"
import { generateId } from "@/lib/id"
import { getProjectIcon } from "@/lib/projects"
import { mutateOk, useZero } from "@/lib/zero"

interface PickItem {
	type: "project" | "issue"
	id: string
	label: string
	identifier?: string
	icon?: string | null
	color?: string | null
	status?: string
	statusColor?: string | null
}

const isPublished = (item: { isVisible?: boolean | null }) =>
	item.isVisible === true || item.isVisible === false

export const CompanyRequestPicker: FC<{ companyId: string }> = memo(
	({ companyId }) => {
		const zero = useZero()
		const utils = api.useUtils()
		const [open, setOpen] = useState(false)
		const [query, setQuery] = useState("")

		// The whole roadmap is only needed once the picker opens.
		const [projects] = useQuery(queries.roadmapProjects({ tab: "all" }), {
			enabled: open,
		})
		const [issues] = useQuery(queries.roadmapIssues({ tab: "all" }), {
			enabled: open,
		})
		const [upvotes] = useQuery(queries.companyUpvotedItems(companyId), {
			enabled: open,
		})

		const upvotedItemIds = useMemo(() => {
			const ids = new Set<string>()
			for (const feedback of upvotes) {
				for (const project of feedback.projects ?? []) ids.add(project.id)
				for (const issue of feedback.issues ?? []) ids.add(issue.id)
			}
			return ids
		}, [upvotes])

		const items = useMemo<PickItem[]>(() => {
			const projectItems: PickItem[] = projects
				.filter(isPublished)
				.map((p) => ({
					type: "project",
					id: p.id,
					label: p.name,
					icon: p.icon,
					color: p.color,
				}))
			const issueItems: PickItem[] = issues.filter(isPublished).map((i) => ({
				type: "issue",
				id: i.id,
				label: i.title,
				identifier: i.identifier,
				status: i.status,
				statusColor: i.statusColor,
			}))
			return [...projectItems, ...issueItems]
		}, [projects, issues])

		const filteredItems = useMemo(() => {
			if (!open) return []
			if (!query)
				return [...items].sort((a, b) => a.label.localeCompare(b.label))
			const fuse = new Fuse(items, {
				keys: ["label", "identifier"],
				threshold: 0.4,
			})
			return fuse.search(query).map((r) => r.item)
		}, [open, items, query])

		const toggle = async (item: PickItem, isSelected: boolean) => {
			const link =
				item.type === "project" ? { projectId: item.id } : { issueId: item.id }
			if (isSelected) {
				await mutateOk(
					zero.mutate(
						mutators.threads.deleteCompanyUpvote({ companyId, ...link }),
					),
				)
			} else {
				await mutateOk(
					zero.mutate(
						mutators.threads.createCompanyUpvote({
							id: generateId(),
							companyId,
							...link,
						}),
					),
				)
			}
			// The preview list is served by tRPC; refresh it after a Zero mutation.
			await utils.portal.getCompanyPortalRequests.invalidate({ companyId })
		}

		return (
			<Popover
				open={open}
				onOpenChange={setOpen}
				align="end"
				className="w-80 p-0"
				content={
					<Command shouldFilter={false}>
						<CommandInput
							showIcon
							placeholder="Search issues and projects..."
							value={query}
							onValueChange={setQuery}
						/>
						<CommandList>
							{filteredItems.length ? (
								<div className="h-72 p-1.5">
									<FixedSizeList
										height={280}
										width="100%"
										itemCount={filteredItems.length}
										itemData={filteredItems}
										itemKey={(index, data) => data[index]?.id ?? index}
										itemSize={36}
										overscanCount={5}
										className="!h-full"
									>
										{({ data, index, style }) => {
											const item = data[index]
											if (!item) return null
											const isSelected = upvotedItemIds.has(item.id)
											const ProjectIcon =
												item.type === "project"
													? getProjectIcon(item.icon)
													: null
											const IssueIcon =
												item.type === "issue"
													? IconMap[item.status ?? ""]
													: null
											return (
												<CommandItem
													value={item.id}
													key={item.id}
													style={style}
													onSelect={() => {
														void toggle(item, isSelected)
													}}
												>
													<div className="flex w-full items-center gap-2">
														{ProjectIcon && (
															<ProjectIcon
																className="h-4 w-4 shrink-0"
																style={{ color: item.color ?? undefined }}
															/>
														)}
														{IssueIcon && (
															<IssueIcon
																className="h-4 w-4 shrink-0"
																color={item.statusColor || "currentColor"}
															/>
														)}
														{item.identifier && (
															<span className="shrink-0 tabular-nums text-tertiary">
																{item.identifier}
															</span>
														)}
														<span className="flex-1 truncate">
															{item.label}
														</span>
														<Check
															className={cn(
																"h-4 w-4 shrink-0",
																isSelected ? "opacity-100" : "opacity-0",
															)}
														/>
													</div>
												</CommandItem>
											)
										}}
									</FixedSizeList>
								</div>
							) : (
								<div className="py-4 text-center text-sm text-secondary">
									No requests found
								</div>
							)}
						</CommandList>
					</Command>
				}
			>
				<Button Icon={Plus} variant="outline">
					Add request
				</Button>
			</Popover>
		)
	},
)
CompanyRequestPicker.displayName = "CompanyRequestPicker"
